import type { CSSProperties } from 'react';
import type { problemSection } from '../../../../app/_shared/marketing/landingContent';
import { withEmphasis } from './emphasisText';
import './problemAndWhy.css';

type Pain = (typeof problemSection.pains)[number];

type Props = {
  eyebrow: string;
  h2: string;
  p1Before: string;
  p1Em: string;
  p1After: string;
  pains: readonly Pain[];
  bridgeLead: string;
  bridge: string;
  forYouEyebrow: string;
  forYou: readonly string[];
};

/** Keyed on `pain.id`, never on the copy — reworded titles must not silently drop the icon. */
function PainIcon({ id }: { id: Pain['id'] }) {
  if (id === 'void') {
    // Sent, and nothing returning — an outbound arrow trailing off into dashes.
    return (
      <svg className="problem-card__icon" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M4 12.5 19.5 5l-5 15-2.6-6.2L4 12.5Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        <path
          d="M3 18h3m3 2.5h2.5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          opacity="0.4"
        />
      </svg>
    );
  }
  if (id === 'noise') {
    // Too much arriving at once — stacked bubbles, only the front one legible.
    return (
      <svg className="problem-card__icon" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M8 3h13v8H12l-4 3V3Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
          opacity="0.32"
        />
        <path
          d="M3 8h13v9H7l-4 3V8Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        <path d="M6.5 12h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    );
  }
  // The pathway that used to exist — two people, the link between them broken.
  return (
    <svg className="problem-card__icon" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="5.5" cy="12" r="2.5" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="18.5" cy="12" r="2.5" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M9 12h1.6m2.8 0H15"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        opacity="0.45"
      />
      <path
        d="M12 8.6v-2m0 11v-2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        opacity="0.28"
      />
    </svg>
  );
}

export function ProblemSection({
  eyebrow,
  h2,
  p1Before,
  p1Em,
  p1After,
  pains,
  bridgeLead,
  bridge,
  forYouEyebrow,
  forYou,
}: Props) {
  return (
    <section className="problem-section" aria-labelledby="problem-heading">
      <header className="problem-section__head">
        <p className="ml-text-eyebrow problem-section__eyebrow">{eyebrow}</p>
        <h2
          id="problem-heading"
          className="ml-heading-serif ml-text-heading problem-section__title">
          {h2}
        </h2>
        <p className="problem-section__lead">
          {p1Before}
          <em>{p1Em}</em>
          {p1After}
        </p>
      </header>

      <div className="problem-grid">
        {pains.map((pain, index) => (
          <article
            key={pain.id}
            className="problem-card ml-revealStaggerItem"
            style={{ '--reveal-i': index } as CSSProperties}>
            <PainIcon id={pain.id} />
            <h3 className="ml-heading-sans problem-card__title">{pain.title}</h3>
            <p className="problem-card__body">{withEmphasis(pain.body)}</p>
          </article>
        ))}
      </div>

      <div className="problem-section__bridge">
        <p className="problem-section__bridgeLead ml-heading-serif">{bridgeLead}</p>
        <p className="problem-section__bridgeBody">{withEmphasis(bridge)}</p>
      </div>

      <div className="problem-forYou">
        <p className="ml-text-eyebrow problem-forYou__eyebrow">{forYouEyebrow}</p>
        <ul className="problem-forYou__list">
          {forYou.map((line) => (
            <li key={line} className="problem-forYou__item">
              {line}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
